import { useGetChecklistTemplatesQuery } from '@/services/checklistTemplates';
import { type DataTableColumn, PalmettoDataTable, PalmettoTag } from '@palmetto/dsm-components';
import { Link } from 'react-router-dom';

import { ChecklistTemplateDocument, Clientize, QueueTypeLabel } from 'types';

import { ProgramTypeTags } from '../shared/ProgramTypeTags';

import { ChecklistAdminLayout } from './ChecklistAdminLayout';

type TemplateRow = Clientize<ChecklistTemplateDocument> & { createdByName?: string; draftId?: string };

const EMPTY_TEMPLATES: TemplateRow[] = [];
const adHocLabel = 'Ad-hoc';

const columns: DataTableColumn<TemplateRow>[] = [
  {
    id: 'name',
    header: 'Name',
    cellType: 'link',
    render: (row) => (
      <div className="flex items-center gap-8">
        <Link to={`/admin/checklists/${row.id}`}>{row.name}</Link>
        {row.draftId && <PalmettoTag label="Draft" color="grey" size="sm" />}
      </div>
    ),
  },
  {
    id: 'queueType',
    header: 'Queue Type',
    width: '200px',
    render: (row) =>
      row.queueType ? QueueTypeLabel[row.queueType as keyof typeof QueueTypeLabel] || row.queueType : adHocLabel,
  },
  {
    id: 'programTypes',
    header: 'Program Types',
    width: '300px',
    cellType: 'tag',
    render: (row) => <ProgramTypeTags programTypes={row.programTypes} />,
  },
  {
    id: 'version',
    header: 'Version',
    width: '100px',
  },
];

const getRowId = (row: TemplateRow) => row.id;

export const ChecklistTemplates = () => {
  const { data, isLoading, isFetching, error, refetch } = useGetChecklistTemplatesQuery({ isCurrent: true });

  const templates = data?.data ?? EMPTY_TEMPLATES;

  return (
    <ChecklistAdminLayout>
      <div className="overflow-x-auto">
        <PalmettoDataTable
          columns={columns}
          data={templates}
          getRowId={getRowId}
          isLoading={isLoading || isFetching}
          error={error}
          onRetry={refetch}
          ariaLabel="Checklist templates"
        />
      </div>
    </ChecklistAdminLayout>
  );
};
